import * as THREE from "three";
import type { GlassPaneDef } from "@rietz/shared";
import { GLASS_PANE_COLOR } from "./palette.js";

/**
 * The window panes, as one `InstancedMesh` over a unit box — a pane is a
 * collision solid until somebody breaks it, and its id is its INDEX in
 * `map.glass`, which is also its instance index here. Breaking one zeroes its
 * matrix; the frame around it is the `casement` prop and stays.
 *
 * The glazing is inset 5 mm on every axis so it never z-fights the sill or the
 * reveal it sits in. No DOM, so it constructs under plain node.
 */

/** Shrink on every axis, metres — see the header. */
const INSET_M = 0.005;

/** Transparent enough to see a room through, opaque enough to read as glass. */
const OPACITY = 0.35;

export class GlassPanes {
  readonly group: THREE.Object3D;

  private readonly geo: THREE.BoxGeometry;
  private readonly mat: THREE.MeshStandardMaterial;
  private readonly mesh: THREE.InstancedMesh;
  private readonly count: number;

  /** Scratch for `reset` — building a map must not allocate per pane. */
  private readonly sPos = new THREE.Vector3();
  private readonly sQuat = new THREE.Quaternion();
  private readonly sScale = new THREE.Vector3(1, 1, 1);
  private readonly sMat = new THREE.Matrix4();

  private readonly panes: readonly GlassPaneDef[];

  constructor(panes: readonly GlassPaneDef[]) {
    this.panes = panes;
    this.count = panes.length;

    this.geo = new THREE.BoxGeometry(1, 1, 1);
    // `depthWrite: false` so two panes in one window frame do not carve holes
    // in each other; double-sided because a pane is seen from both rooms.
    this.mat = new THREE.MeshStandardMaterial({
      color: GLASS_PANE_COLOR,
      transparent: true,
      opacity: OPACITY,
      roughness: 0.15,
      metalness: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    this.mesh = new THREE.InstancedMesh(this.geo, this.mat, Math.max(this.count, 1));
    this.mesh.name = "glass";
    // Panes are scattered over the whole map, and a broken one is a degenerate
    // matrix — a bounding sphere over that is permanently wrong.
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = this.count;

    this.reset([]);

    this.group = this.mesh;
  }

  /** Hides one pane for good: a zero-scale matrix costs the rasteriser nothing. */
  break(id: number): void {
    if (!Number.isInteger(id) || id < 0 || id >= this.count) return;
    this.sMat.makeScale(0, 0, 0);
    this.mesh.setMatrixAt(id, this.sMat);
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * Puts every pane back and re-breaks the ones in `broken` — the join-time
   * `glassState`, and the only way a pane is ever restored.
   */
  reset(broken: Iterable<number>): void {
    for (let i = 0; i < this.count; i++) {
      const pane = this.panes[i];
      if (pane === undefined) continue;
      const b = pane.box;
      this.sPos.set(
        (b.min.x + b.max.x) * 0.5,
        (b.min.y + b.max.y) * 0.5,
        (b.min.z + b.max.z) * 0.5,
      );
      this.sScale.set(
        Math.max(b.max.x - b.min.x - INSET_M * 2, 1e-4),
        Math.max(b.max.y - b.min.y - INSET_M * 2, 1e-4),
        Math.max(b.max.z - b.min.z - INSET_M * 2, 1e-4),
      );
      this.sMat.compose(this.sPos, this.sQuat, this.sScale);
      this.mesh.setMatrixAt(i, this.sMat);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    for (const id of broken) this.break(id);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geo.dispose();
    this.mat.dispose();
  }
}
